<template>
  <HrPageLayout
    title="Off Day Approvals"
    subtitle="Review and approve employee off-day requests"
    icon="✅"
    :breadcrumb-items="[{ label: 'Leave Management' }, { label: 'Off Day Approvals' }]"
  >
    <template #header-actions>
      <select v-model="status" class="hr-select" style="width: 180px;" @change="loadRequests">
        <option value="">All</option>
        <option value="Pending">Pending</option>
        <option value="Approved">Approved</option>
        <option value="Rejected">Rejected</option>
      </select>
    </template>

    <div class="hr-metrics-grid hr-mb-4">
      <div class="hr-metric-card gradient-4">
        <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
        <p class="hr-metric-label">Pending</p>
        <h2 class="hr-metric-value">{{ metrics.pendingRequests || 0 }}</h2>
      </div>
      <div class="hr-metric-card gradient-2">
        <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
        <p class="hr-metric-label">Approved</p>
        <h2 class="hr-metric-value">{{ metrics.approvedRequests || 0 }}</h2>
      </div>
    </div>

    <div class="hr-chart-card">
      <div class="hr-chart-header">
        <h5 class="hr-chart-title"><i class="bx bx-list-ul"></i> Off Day Requests</h5>
        <input v-model="search" type="text" class="hr-input" placeholder="Search..." style="max-width:260px;" @keyup.enter="loadRequests">
      </div>
      <div class="hr-chart-body">
        <div v-if="loading" class="hr-loading-state"><div class="hr-spinner"></div><p>Loading...</p></div>
        <div v-else-if="!requests.length" class="hr-empty-state"><i class="bx bx-file"></i><h5>No requests</h5></div>
        <div v-else class="hr-table-wrapper">
          <table class="hr-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Program</th>
                <th>Dates</th>
                <th>Days</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Approved by</th>
                <th style="text-align:center;">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in requests" :key="r.id">
                <td>{{ r.names }}</td>
                <td>{{ r.program_name }}</td>
                <td>{{ formatDate(r.start_date) }} – {{ formatDate(r.end_date) }}</td>
                <td><span class="hr-badge hr-badge-info">{{ r.total_days }}</span></td>
                <td><span class="text-truncate-cell" :title="r.reason">{{ r.reason }}</span></td>
                <td><span class="hr-badge" :class="statusClass(r.status)">{{ r.status }}</span></td>
                <td>
                  <span v-if="isApproved(r.status)" class="small">
                    {{ r.approved_by || '—' }}
                    <br v-if="r.approval_date">
                    <span v-if="r.approval_date" class="text-muted">{{ formatDate(r.approval_date) }}</span>
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td style="text-align:center;">
                  <template v-if="isPending(r.status)">
                    <button class="hr-btn hr-btn-success hr-btn-sm" :disabled="r.processing" @click="process(r, 'Approved')">Approve</button>
                    <button class="hr-btn hr-btn-danger hr-btn-sm" :disabled="r.processing" @click="process(r, 'Rejected')">Reject</button>
                  </template>
                  <span v-else class="text-muted small">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from '@/components/hrActions/HrPageLayout.vue';
import { authUrl, execute } from '@/api';

export default {
  name: 'OffDayApprovals',
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      status: '',
      search: '',
      requests: [],
      metrics: {}
    };
  },
  mounted() {
    this.loadRequests();
  },
  methods: {
    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    isPending(s) {
      return String(s).toLowerCase() === 'pending';
    },
    isApproved(s) {
      return String(s).toLowerCase() === 'approved';
    },
    statusClass(s) {
      const x = String(s).toLowerCase();
      if (x === 'approved') return 'hr-badge-success';
      if (x === 'rejected') return 'hr-badge-danger';
      return 'hr-badge-warning';
    },
    async loadRequests() {
      this.loading = true;
      const data = new FormData();
      data.append('function', 'getOffDayRequests');
      data.append('status', this.status);
      data.append('search', this.search);
      data.append('limit', 100);
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.requests = (res.data.data?.requests || []).map(r => ({ ...r, processing: false }));
          this.metrics = res.data.data?.metrics || {};
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async process(item, action) {
      const remarks = action === 'Rejected' ? (prompt('Rejection reason (optional):') || '') : '';
      item.processing = true;
      const data = new FormData();
      data.append('function', 'approveOrRejectOffDay');
      data.append('requestId', item.id);
      data.append('action', action);
      data.append('remarks', remarks);
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          item.status = action;
        } else {
          alert(res.data.message || 'Failed');
        }
      } catch (e) {
        alert('Request failed');
      } finally {
        item.processing = false;
        this.loadRequests();
      }
    }
  }
};
</script>
